import { useState } from "react";
import { useLocation } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { submitLead, getUtm, trackEvent } from "../lib/api";

const INTERESTS = [
  { v: "gnts1", label: "GNTS1 Bluetooth Smart Sticker" },
  { v: "ll100", label: "LL100 4G Smart Label" },
  { v: "both", label: "Both / help me compare" },
  { v: "not-sure", label: "Not sure yet" },
];

const VOLUMES = ["Under 100", "100–1,000", "1,000–10,000", "10,000+", "Not sure yet"];
const INDUSTRIES = ["Pharma / cold chain", "High-value cargo / electronics", "Freight & logistics", "Perishables / food", "Asset & packaging tracking", "Other"];

const inputCls =
  "w-full rounded-sm border border-input bg-white px-3 py-2.5 text-sm text-brand-navy placeholder:text-slate-400 transition-colors duration-150 focus:border-primary";
const labelCls = "mb-1.5 block text-sm font-medium text-brand-navy";

export default function LeadForm({ defaultInterest = "not-sure" }) {
  const location = useLocation();
  const [stage, setStage] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [f, setF] = useState({
    name: "", email: "", company: "", role: "",
    interest: defaultInterest, industry: "", monthly_volume: "", air_freight: "",
    message: "", consent: false, website: "",
  });

  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const stage0Valid = f.name.trim() && /.+@.+\..+/.test(f.email) && f.company.trim();

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await submitLead({ ...f, page: location.pathname, utm: getUtm() });
      trackEvent("form_submitted", { interest: f.interest, page: location.pathname });
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Something went wrong sending your enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-sm border border-lime-600/40 bg-lime-50 p-8" data-testid="lead-form-success" role="status">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-1 h-6 w-6 flex-shrink-0 text-lime-700" aria-hidden="true" />
          <div>
            <h2 className="text-xl font-semibold text-brand-navy">Enquiry received — thank you, {f.name.split(" ")[0]}.</h2>
            <p className="mt-2 text-sm leading-relaxed text-brand-slate">
              We've emailed a confirmation to <strong>{f.email}</strong>. A member of the GoAndTrack team will
              review your shipment profile and respond, usually within one business day.
            </p>
            <p className="mt-2 text-sm text-brand-slate">
              Nothing is being sold to you yet — the next step is a short scoping call to see if a pilot makes sense.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-sm border border-border bg-white p-6 sm:p-8" data-testid="lead-form" noValidate>
      <div className="mb-6 flex items-center gap-2" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <span key={i} className={`h-1 flex-1 rounded-full ${i <= stage ? "bg-primary" : "bg-border"}`} />
        ))}
      </div>
      <p className="mb-5 font-mono text-xs uppercase tracking-widest text-brand-slate" aria-live="polite">
        Step {stage + 1} of 3 — {stage === 0 ? "You" : stage === 1 ? "Your shipments" : "Consent & send"}
      </p>

      {stage === 0 && (
        <div className="space-y-5" data-testid="lead-stage-1">
          <div>
            <label htmlFor="lf-name" className={labelCls}>Full name *</label>
            <input id="lf-name" data-testid="lead-name-input" className={inputCls} value={f.name} onChange={set("name")} autoComplete="name" required />
          </div>
          <div>
            <label htmlFor="lf-email" className={labelCls}>Work email *</label>
            <input id="lf-email" type="email" data-testid="lead-email-input" className={inputCls} value={f.email} onChange={set("email")} autoComplete="email" required />
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="lf-company" className={labelCls}>Company *</label>
              <input id="lf-company" data-testid="lead-company-input" className={inputCls} value={f.company} onChange={set("company")} autoComplete="organization" required />
            </div>
            <div>
              <label htmlFor="lf-role" className={labelCls}>Role</label>
              <input id="lf-role" data-testid="lead-role-input" className={inputCls} value={f.role} onChange={set("role")} placeholder="e.g. Logistics Manager" autoComplete="organization-title" />
            </div>
          </div>
          <button
            type="button"
            onClick={() => setStage(1)}
            disabled={!stage0Valid}
            data-testid="lead-next-1"
            className="rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Continue
          </button>
        </div>
      )}

      {stage === 1 && (
        <div className="space-y-5" data-testid="lead-stage-2">
          <fieldset>
            <legend className={labelCls}>Which product are you leaning towards?</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {INTERESTS.map((o) => (
                <label key={o.v} className={`flex cursor-pointer items-center gap-2 rounded-sm border p-3 text-sm transition-colors duration-150 ${f.interest === o.v ? "border-primary bg-blue-50 text-brand-navy" : "border-border text-brand-slate hover:border-primary/50"}`}>
                  <input type="radio" name="interest" value={o.v} checked={f.interest === o.v} onChange={set("interest")} data-testid={`lead-interest-${o.v}`} className="accent-blue-600" />
                  {o.label}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="lf-industry" className={labelCls}>Industry</label>
              <select id="lf-industry" data-testid="lead-industry-select" className={inputCls} value={f.industry} onChange={set("industry")}>
                <option value="">Select…</option>
                {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="lf-volume" className={labelCls}>Shipments per month</label>
              <select id="lf-volume" data-testid="lead-volume-select" className={inputCls} value={f.monthly_volume} onChange={set("monthly_volume")}>
                <option value="">Select…</option>
                {VOLUMES.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>
          <fieldset>
            <legend className={labelCls}>Does any of this freight move by air?</legend>
            <div className="flex gap-4">
              {["yes", "no", "unsure"].map((v) => (
                <label key={v} className="flex cursor-pointer items-center gap-2 text-sm text-brand-slate">
                  <input type="radio" name="air" value={v} checked={f.air_freight === v} onChange={set("air_freight")} data-testid={`lead-air-${v}`} className="accent-blue-600" />
                  {v === "unsure" ? "Not sure" : v[0].toUpperCase() + v.slice(1)}
                </label>
              ))}
            </div>
            {f.air_freight === "yes" && (
              <p className="mt-3 rounded-sm border border-amber-300 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900" data-testid="lead-air-note">
                Noted — airline acceptance and flight-mode rules differ per device and carrier. We'll cover this in the scoping call before anything ships.
              </p>
            )}
          </fieldset>
          <div>
            <label htmlFor="lf-message" className={labelCls}>Tell us about the shipment problem you're trying to solve</label>
            <textarea id="lf-message" data-testid="lead-message-input" className={`${inputCls} min-h-[100px]`} value={f.message} onChange={set("message")} placeholder="Lanes, product type, what's gone wrong before…" />
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => setStage(0)} data-testid="lead-back-1" className="rounded-sm border border-border px-6 py-3 text-sm font-semibold text-brand-slate hover:border-primary hover:text-primary">Back</button>
            <button type="button" onClick={() => setStage(2)} data-testid="lead-next-2" className="rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-blue-700">Continue</button>
          </div>
        </div>
      )}

      {stage === 2 && (
        <div className="space-y-5" data-testid="lead-stage-3">
          <div className="rounded-sm bg-brand-surface p-4 text-sm text-brand-slate">
            <p><strong className="text-brand-navy">{f.name}</strong> · {f.company} · {f.email}</p>
            <p className="mt-1">Interest: {INTERESTS.find((i) => i.v === f.interest)?.label}{f.industry ? ` · ${f.industry}` : ""}{f.monthly_volume ? ` · ${f.monthly_volume}/month` : ""}</p>
          </div>
          <label className="flex cursor-pointer items-start gap-3 text-sm text-brand-slate" data-testid="lead-consent-label">
            <input type="checkbox" checked={f.consent} onChange={set("consent")} data-testid="lead-consent-checkbox" className="mt-0.5 h-4 w-4 accent-blue-600" required />
            <span>I consent to GoAndTrack storing these details and contacting me about my enquiry, as described in the <a href="/privacy" className="text-primary underline underline-offset-2">privacy policy</a>. *</span>
          </label>
          <input
            type="text" name="website" value={f.website} onChange={set("website")} tabIndex={-1} autoComplete="off"
            aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 opacity-0" data-testid="lead-honeypot"
          />
          {error && <p className="rounded-sm border border-red-300 bg-red-50 p-3 text-sm text-red-800" role="alert" data-testid="lead-form-error">{error}</p>}
          <div className="flex gap-3">
            <button type="button" onClick={() => setStage(1)} data-testid="lead-back-2" className="rounded-sm border border-border px-6 py-3 text-sm font-semibold text-brand-slate hover:border-primary hover:text-primary">Back</button>
            <button
              type="submit"
              disabled={!f.consent || submitting}
              data-testid="lead-submit"
              className="inline-flex items-center gap-2 rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {submitting ? "Sending…" : "Send enquiry"}
            </button>
          </div>
        </div>
      )}
    </form>
  );
}
